import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";

import { makeStyles } from "@material-ui/core/styles";

import { updateAlarmAction, updateAllAlarmsAction } from "store/actions/polling";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { markAllAlarmsRead } from "api/alarmsList";

import { AlarmPageStrings, HEADER_STRINGS } from "constants/constantStrings";

import BadgeIcon from "components/BadgeIcon";
import DupontButton from "components/DupontButton";
import ToggleSort from "components/ToggleSort";

import CircularIcon from "assets/Alarms/alert-circular-icon.svg";
import AlertTriangleLarge from "assets/Alarms/alert-triangle-green-large.svg";
import AlarmsNotificationsIcon from "assets/SvgIcon/Header/alarm_notifications_icon";

import NotificationItem from "../LiveAlarmsNotifications/NotificationItem";

import "./index.scss";

const useStyles = makeStyles(() => ({
  modal: {
    display: "flex",
    marginTop: 49,
    backgroundColor: "white",
    width: "300px",
    marginLeft: "auto"
  },
  paper: {
    backgroundColor: "white",
    borderRadius: "1px",
    width: "300px",
    minHeight: "110px",
    outline: "none"
  }
}));

export default function LiveAlarmsNotifications() {
  const classes = useStyles();
  const activeAlarms = useSelector(state => state.poll.activeAlarms);
  const hasSystemsAccess = useSelector(state => state.user.hasSystemsAccess);
  const dispatch = useDispatch();
  const [alarmsData, setAlarmsData] = useState(activeAlarms);

  const [isAlarmsDropdownOpen, setIsAlarmsDropdownOpen] = useState(false);
  const [numberUnreadAlarms, setNumberUnreadAlarms] = useState(null);
  const [badgeIconCustomClass, setBadgeIconCustomClass] = useState("");
  const [isAllAlarmsReadLinkDisable, setIsAllAlarmsReadLinkDisable] = useState(false);
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    if (hasSystemsAccess) {
      updateUnreadAlarmsCount(activeAlarms);
    }
    setAlarmsData(activeAlarms);
    const isAnyAlarmUnread = activeAlarms.some(alarm => !alarm.readStatus);
    setIsAllAlarmsReadLinkDisable(!isAnyAlarmUnread);
  }, [activeAlarms]);

  const toggleSorting = () => {
    objAppInsights.trackEvent("alarmsToggleSorting");
    setAlarmsData([...alarmsData].reverse());
  };

  const updateUnreadAlarmsCount = alarms => {
    if (alarms) {
      const unreadAlarms = alarms.filter(alarm => alarm.readStatus === 0);
      setNumberUnreadAlarms(unreadAlarms.length);
    }
  };

  const updateReadStatus = (sysId, alarmId, readStatus) => dispatch(updateAlarmAction({ sysId, alarmId, readStatus }));

  const onAlarmsDropdownClick = () => {
    objAppInsights.trackEvent("alarmsDropDownOpen");
    if (isAlarmsDropdownOpen === false) {
      setIsAlarmsDropdownOpen(true);
      setBadgeIconCustomClass("badge-icon-green");
    } else {
      setIsAlarmsDropdownOpen(false);
      setBadgeIconCustomClass("");
    }
  };

  const handleMarkAllRead = async () => {
    objAppInsights.trackEvent("markAllAlarmsReadClicked");
    const updatedAlarms = activeAlarms.map(alarm => ({ ...alarm, readStatus: 1 }));
    try {
      objAppInsights.trackEvent("markAllAlarmsReadToggled");
      setIsAllAlarmsReadLinkDisable(true);
      await markAllAlarmsRead(true);
      dispatch(updateAllAlarmsAction(updatedAlarms));
    } catch (err) {
      setIsAllAlarmsReadLinkDisable(false);
    }
  };

  return (
    <div>
      <Dropdown className="margin-left-10 pointer" isOpen={isAlarmsDropdownOpen} toggle={onAlarmsDropdownClick}>
        <DropdownToggle tag="span">
          <BadgeIcon
            customClass={badgeIconCustomClass}
            iconComponent={<AlarmsNotificationsIcon />}
            open={isAlarmsDropdownOpen}
            badgeText={numberUnreadAlarms}
            tooltip={AlarmPageStrings.alarmsNotifications}
          />
        </DropdownToggle>

        <DropdownMenu right className="dropStyleMenu">
          <div className="alarms__modal">
            <div className={classes.paper}>
              {alarmsData.length ? (
                <>
                  <div className="whiteBackground calculatedValueText border-bottom__light-grey padding15">
                    {HEADER_STRINGS.activeAlarms}
                    <div className="flex-between-center">
                      <DupontButton type="tertiary" customClass="mark-all-read-btn" disabled={isAllAlarmsReadLinkDisable} onClick={handleMarkAllRead}>
                        {AlarmPageStrings.markAllRead}
                      </DupontButton>
                      <ToggleSort toggleSorting={toggleSorting} />
                    </div>
                  </div>
                  <ul className="whiteBackground flex-column ulActiveAlarm">
                    {alarmsData &&
                      alarmsData.map(alarm => (
                        <NotificationItem key={alarm.id} notification={alarm} onClick={onAlarmsDropdownClick} parentCallback={updateReadStatus} />
                      ))}
                    <div className="whiteBackground alarm-modal__messagebox grayColor font12">
                      <div>
                        <img className="margin-auto" src={CircularIcon} alt="alert-circular-icon" />
                      </div>
                      <div>{AlarmPageStrings.alarmModalEndText}</div>
                    </div>
                  </ul>
                </>
              ) : (
                <div className="alarm-modal-noalarms__container">
                  <img className="margin-right-5" src={AlertTriangleLarge} alt="alert-triangle" />
                  <div className="alarms__card--title alarm-modal-noalarms__heading">{AlarmPageStrings.alarmModalNoAlarmsHeading}</div>
                  <div className="grayText font12 alarm-modal-noalarms__text">{AlarmPageStrings.alarmModalNoAlarms}</div>
                </div>
              )}
            </div>
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}
